import { useState, useEffect, useRef, useCallback } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { StepDecisionInput } from '@/components/onboarding/StepDecisionInput';
import { StepClassificationConfirm, type ClassificationConfirmMeta } from '@/components/onboarding/StepClassificationConfirm';
import { StepSituation } from '@/components/onboarding/StepSituation';
import { StepAnalysisPreview } from '@/components/onboarding/StepAnalysisPreview';
import { IntakeFlow } from '@/components/intake/IntakeFlow';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { useDecisionDraft } from '@/hooks/useDecisionDraft';
import { useStartFlowLock } from '@/hooks/useStartFlowLock';
import { SeatPersonalization } from '@/hooks/useSeatPersonalization';
import { supabase } from '@/integrations/supabase/client';
import { Sparkles, ArrowLeft, Target, MessageSquare, Rocket, RotateCcw, PenLine, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { logStartFlowEvent } from '@/lib/telemetry/startFlow';
import QuentariLogo from '@/assets/quentari-logo.svg';

interface NewDecisionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  personalization: SeatPersonalization;
  firstName?: string | null;
}

interface Classification {
  decision_type: string;
  decision_subtype: string;
  confidence: number;
  reasoning: string;
  suggested_title: string;
  classification_signals?: string[];
  preliminary_impact_level?: string;
  affected_roles?: string[];
}

const STEP_CONFIG = [
  { icon: PenLine, label: 'Beschreibung' },
  { icon: Target, label: 'Einordnung' },
  { icon: MessageSquare, label: 'Situation' },
  { icon: Rocket, label: 'Analyse' },
];

export const NewDecisionSheet = ({
  open,
  onOpenChange,
  workspaceId,
  personalization,
  firstName,
}: NewDecisionSheetProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [isAnimating, setIsAnimating] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [isClassifying, setIsClassifying] = useState(false);
  const [classification, setClassification] = useState<Classification | null>(null);
  const [userDescription, setUserDescription] = useState('');
  const [useIntakeFlow, setUseIntakeFlow] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const startFlowIdRef = useRef<string>(crypto.randomUUID());

  const {
    decisionType, decisionSubtype, trigger, stakes, previousAttempts,
    impactScore, urgencyScore, systemClassifiedType, systemClassifiedSubtype,
    classificationConfidence, classificationOverridden, classificationInputHash,
    classificationModel, setDecisionArea, setDecisionSubtype: setStoreDecisionSubtype,
    setClassificationSource, setClassificationOverridden, setSubStep, reset,
  } = useOnboardingStore();

  const { saveDraft, loadDraft, clearDraft, restoreDraft, hasDraft, hasRestoredRef } = useDecisionDraft(workspaceId, open);
  const { acquire: acquireTabLock, release: releaseTabLock, isLockedByOtherTab } = useStartFlowLock(workspaceId);

  // ── Lifecycle ──

  useEffect(() => { if (!open) hasRestoredRef.current = false; }, [open, hasRestoredRef]);

  useEffect(() => {
    if (open && !hasRestoredRef.current) {
      if (isLockedByOtherTab()) {
        toast({ title: "Bereits geöffnet", description: "Der Entscheidungsstart ist bereits in einem anderen Tab aktiv.", variant: "destructive" });
        onOpenChange(false);
        return;
      }
      acquireTabLock();
      logStartFlowEvent({ event: 'start_flow_opened', workspaceId });
      if (hasDraft()) {
        setShowDraftDialog(true);
      } else {
        reset(); setCurrentStep(1); setDirection('forward'); setClassification(null); setUserDescription('');
      }
      hasRestoredRef.current = true;
    }
  }, [open, hasDraft, reset, hasRestoredRef, workspaceId, acquireTabLock, isLockedByOtherTab, onOpenChange]);

  useEffect(() => {
    if (open && (currentStep > 1 || userDescription)) {
      saveDraft(currentStep, { userDescription, classification, startFlowId: startFlowIdRef.current });
    }
  }, [open, currentStep, userDescription, classification, decisionType, decisionSubtype, trigger, stakes, previousAttempts, impactScore, urgencyScore, saveDraft]);

  const handleRestoreDraft = useCallback(() => {
    const { draft, currentStep: savedStep } = loadDraft();
    if (draft) {
      restoreDraft(draft);
      if (draft.userDescription) setUserDescription(draft.userDescription);
      if (draft.classification) setClassification(draft.classification);
      if (draft.startFlowId) startFlowIdRef.current = draft.startFlowId;
      setCurrentStep(savedStep); setDirection('forward');
      toast({ title: "Entwurf wiederhergestellt", description: "Dein vorheriger Fortschritt wurde geladen." });
    }
    setShowDraftDialog(false);
  }, [loadDraft, restoreDraft]);

  const handleDiscardDraft = useCallback(() => {
    clearDraft(); reset(); startFlowIdRef.current = crypto.randomUUID();
    setCurrentStep(1); setDirection('forward'); setClassification(null); setUserDescription('');
    setShowDraftDialog(false);
  }, [clearDraft, reset]);

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 400);
    return () => clearTimeout(timer);
  }, [currentStep]);

  const hasUnsavedData = currentStep > 1 || decisionType || trigger || stakes || previousAttempts || userDescription;

  const handleCloseRequest = (openState: boolean) => {
    if (!openState && hasUnsavedData) {
      setShowCloseConfirm(true);
    } else if (!openState) {
      releaseTabLock(); clearDraft(); reset(); startFlowIdRef.current = crypto.randomUUID();
      setCurrentStep(1); setClassification(null); setUserDescription(''); onOpenChange(false);
    } else {
      onOpenChange(openState);
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    logStartFlowEvent({ event: 'start_flow_abandoned', workspaceId, metadata: { step: currentStep } });
    releaseTabLock(); clearDraft(); reset(); startFlowIdRef.current = crypto.randomUUID();
    setCurrentStep(1); setClassification(null); setUserDescription(''); onOpenChange(false);
  };

  // ── Classic flow handlers ──

  const handleDescriptionSubmit = async (description: string) => {
    setUserDescription(description); setIsClassifying(true);
    const classifyRequestId = crypto.randomUUID();
    logStartFlowEvent({ event: 'description_submitted', workspaceId, metadata: { length: description.length, classify_request_id: classifyRequestId } });
    try {
      const { data, error } = await supabase.functions.invoke('classify-decision', { body: { description, request_id: classifyRequestId } });
      if (error) throw error;
      const result: Classification = {
        decision_type: data.decision_type || 'strategy', decision_subtype: data.decision_subtype || 'focus',
        confidence: data.confidence || 50, reasoning: data.reasoning || 'Automatische Einordnung',
        suggested_title: data.suggested_title || description.slice(0, 80),
      };
      const inputHash = data.input_hash || null;
      logStartFlowEvent({ event: 'classification_received', workspaceId, metadata: { type: result.decision_type, subtype: result.decision_subtype, confidence: result.confidence, input_hash: inputHash, cached: data.cached ?? false, classify_request_id: classifyRequestId, system_classified_type: result.decision_type, system_classified_subtype: result.decision_subtype } });
      setClassificationSource(result.decision_type, result.decision_subtype, result.confidence, inputHash, 'google/gemini-2.5-flash');
      const { setClassificationSignals, setPreliminaryImpactLevel, setAffectedRoles } = useOnboardingStore.getState();
      setClassificationSignals(result.classification_signals || []); setPreliminaryImpactLevel(result.preliminary_impact_level || null); setAffectedRoles(result.affected_roles || []);
      setClassification(result); setDirection('forward'); setCurrentStep(2);
    } catch (err: any) {
      console.error('Classification error:', err);
      logStartFlowEvent({ event: 'classification_fallback', workspaceId, metadata: { error: err?.message } });
      setClassification({ decision_type: 'strategy', decision_subtype: 'focus', confidence: 20, reasoning: 'Automatische Einordnung nicht möglich – bitte manuell wählen.', suggested_title: description.slice(0, 80) });
      setDirection('forward'); setCurrentStep(2);
      toast({ title: "Hinweis", description: "Die automatische Einordnung war nicht möglich. Bitte wähle manuell.", variant: "destructive" });
    } finally { setIsClassifying(false); }
  };

  const handleClassificationConfirm = (type: string, subtype: string, meta: ClassificationConfirmMeta) => {
    setDirection('forward'); setDecisionArea(type); setStoreDecisionSubtype(subtype);
    const wasOverride = type !== classification?.decision_type || subtype !== classification?.decision_subtype;
    setClassificationOverridden(wasOverride);
    const store = useOnboardingStore.getState();
    store.setAffectedRoles(meta.affectedRoles); store.setPreliminaryImpactLevel(meta.impactLevel);
    store.setClassificationSignals(meta.signals); store.setSessionTitle(meta.title);
    logStartFlowEvent({ event: wasOverride ? 'classification_overridden' : 'classification_confirmed', workspaceId, metadata: { final_type: type, final_subtype: subtype, was_override: wasOverride, system_classified_type: classification?.decision_type, system_classified_subtype: classification?.decision_subtype, system_confidence: classification?.confidence, affected_roles: meta.affectedRoles, impact_level: meta.impactLevel, override_delta: wasOverride ? { type_changed: type !== classification?.decision_type, subtype_changed: subtype !== classification?.decision_subtype, from_type: classification?.decision_type, to_type: type, from_subtype: classification?.decision_subtype, to_subtype: subtype } : null } });
    setCurrentStep(3);
  };

  const handleSituationComplete = () => { setDirection('forward'); logStartFlowEvent({ event: 'situation_submitted', workspaceId }); setCurrentStep(4); };
  const handleBack = () => { if (currentStep > 1) { setDirection('backward'); setCurrentStep(currentStep - 1); } };
  const handleIntakeComplete = () => { setDirection('forward'); setCurrentStep(4); };
  const handleSwitchToClassic = useCallback(() => { setUseIntakeFlow(false); setCurrentStep(1); }, []);

  const renderStep = () => {
    if (useIntakeFlow && currentStep < 4) {
      return <IntakeFlow workspaceId={workspaceId} onComplete={handleIntakeComplete} startFlowId={startFlowIdRef.current} onSwitchToClassic={handleSwitchToClassic} />;
    }
    switch (currentStep) {
      case 1: return <StepDecisionInput initialValue={userDescription} onSubmit={handleDescriptionSubmit} isClassifying={isClassifying} />;
      case 2: return classification ? <StepClassificationConfirm classification={classification} userDescription={userDescription} workspaceId={workspaceId} onConfirm={handleClassificationConfirm} onBack={handleBack} /> : null;
      case 3: return decisionType && decisionSubtype ? <StepSituation workspaceId={workspaceId} role={personalization.job_role || ''} title={personalization.job_title || undefined} decisionType={decisionType} decisionSubtype={decisionSubtype} onComplete={handleSituationComplete} /> : null;
      case 4: return workspaceId && decisionType && decisionSubtype && trigger ? <StepAnalysisPreview workspaceId={workspaceId} role={personalization.job_role || ''} title={personalization.job_title || undefined} decisionType={decisionType} decisionSubtype={decisionSubtype} trigger={trigger} stakes={stakes} previousAttempts={previousAttempts} impactScore={impactScore} urgencyScore={urgencyScore} impactMetric={useOnboardingStore.getState().impactMetric} urgencyReason={useOnboardingStore.getState().urgencyReason} systemClassifiedType={systemClassifiedType} systemClassifiedSubtype={systemClassifiedSubtype} classificationConfidence={classificationConfidence} classificationOverridden={classificationOverridden} classificationInputHash={classificationInputHash} classificationModel={classificationModel} startFlowId={startFlowIdRef.current} onEditStep={(stepIndex) => { setDirection('backward'); setSubStep(stepIndex); setCurrentStep(3); }} /> : null;
      default: return null;
    }
  };

  const getAnimationClass = () => {
    if (!isAnimating) return 'opacity-100 translate-x-0';
    return direction === 'forward' ? 'animate-slide-in-from-right' : 'animate-slide-in-from-left';
  };

  const isFullscreenIntake = useIntakeFlow && currentStep < 4 && open;

  return (
    <>
      {/* ═══════════════════════════════════════ */}
      {/* FULLSCREEN INTAKE MODE                  */}
      {/* ═══════════════════════════════════════ */}
      {isFullscreenIntake && (
        <div className="fixed inset-0 z-50 bg-background overflow-hidden">
          {/* Subtle ambient background */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/[0.02] rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/[0.015] rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
          </div>

          {/* Header — matching dashboard h-16 */}
          <header className="sticky top-0 z-20 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border h-16">
            <div className="container flex h-full items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-4">
                <img src={QuentariLogo} alt="Quentari" className="h-8 w-auto transition-transform duration-300 hover:scale-105" />
                <div className="w-px h-5 bg-border/50" />
                <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground/40 font-mono">
                  Decision Engine
                </span>
              </div>
              <Button
                variant="ghost" size="icon"
                onClick={() => handleCloseRequest(false)}
                className="h-9 w-9 rounded-lg text-muted-foreground/50 hover:text-foreground hover:bg-muted/40 transition-all"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </header>

          {/* Content — centered */}
          <div className="relative z-10 overflow-y-auto" style={{ height: 'calc(100vh - 64px)' }}>
            <div className="flex items-start justify-center min-h-full">
              <div className="w-full max-w-5xl mx-auto px-6 py-12">
                {renderStep()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════ */}
      {/* SHEET MODE (Step 4 / Classic)            */}
      {/* ═══════════════════════════════════════ */}
      <Sheet open={open && !isFullscreenIntake} onOpenChange={handleCloseRequest}>
        <SheetContent side="right" className={cn("w-full overflow-y-auto border-border/50 bg-background p-0", currentStep === 3 || currentStep === 4 ? "sm:max-w-4xl" : "sm:max-w-2xl")}>
          <div className="relative z-10">
            {/* Header */}
            <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border">
              <div className="px-6 py-4 pb-5">
                <div className="flex items-center gap-3">
                  {currentStep > 1 && currentStep !== 4 && (
                    <Button variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8 rounded-lg bg-muted/60 hover:bg-muted transition-all shrink-0">
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                  )}
                  <div className="flex-1 min-w-0">
                    <SheetDescription className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-0.5">Decision Process</SheetDescription>
                    <SheetTitle className="text-xl font-semibold truncate transition-all duration-300 leading-tight">
                      {currentStep === 1 ? 'Entscheidung beschreiben' : currentStep === 2 ? 'Einordnung bestätigen' : currentStep === 3 ? 'Situation beschreiben' : 'Analyse starten'}
                    </SheetTitle>
                  </div>
                </div>

                {/* Step navigation — classic mode only */}
                <div className="mt-5 max-w-[720px] mx-auto">
                  <div className="flex mb-2">
                    {STEP_CONFIG.map((step, index) => {
                      const stepNum = index + 1;
                      const isActive = stepNum === currentStep;
                      const isCompleted = stepNum < currentStep;
                      return (
                        <div key={index} className="flex-1 text-center">
                          <span className={cn("text-[11px] font-medium transition-colors duration-300", isActive ? "text-foreground" : isCompleted ? "text-foreground/60" : "text-muted-foreground/40")}>
                            {stepNum} {step.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-1.5">
                    {STEP_CONFIG.map((_, index) => {
                      const stepNum = index + 1;
                      return (
                        <div key={index} className={cn("flex-1 h-1 rounded-full transition-all duration-500", stepNum < currentStep ? "bg-primary" : stepNum === currentStep ? "bg-primary" : "bg-border")} />
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-8 pt-12 overflow-hidden">
              <div ref={contentRef} key={currentStep} className={cn("transition-all duration-400 ease-out", getAnimationClass())}>
                {renderStep()}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Dialogs */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eingaben verwerfen?</AlertDialogTitle>
            <AlertDialogDescription>Du hast bereits Daten eingegeben. Wenn du jetzt schließt, gehen alle Eingaben verloren.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zurück</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Verwerfen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDraftDialog} onOpenChange={setShowDraftDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5 text-primary" /> Entwurf gefunden</AlertDialogTitle>
            <AlertDialogDescription>Du hast eine unvollendete Entscheidung. Möchtest du dort weitermachen, wo du aufgehört hast?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDiscardDraft}>Neu starten</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestoreDraft}>Fortsetzen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
